// The Deliveries board's two id-filtered reads (run chips, empties loans).
//
// Both filter on a list of ids bounded by the TENANT's window, not by a screen:
// 1,961 deliveries in 90 days is ~76 KB of `.in()` URL against a ~16 KB limit
// (inChunks.ts). A single `.in()` therefore came back as an error, the query
// fell back to an empty map, and every In Stock card read "Unallocated" (U14
// D4). Read in chunks of IN_CHUNK, and THROW on any error so the caller can say
// "could not read" instead of showing a confident empty.
import { fetchAllPagedIn, inChunks } from '@/lib/inChunks'
import { withReadTimeout } from '@/lib/saveTimeout'

type PageLike<T> = PromiseLike<{ data: T[] | null; error: { message: string } | null }>

// Structural: only the part of the supabase client these reads use.
type Db = { from: (table: string) => any }

export async function readRunAllocationRows<T>(db: Db, deliveryIds: readonly string[]): Promise<T[]> {
  const res = await fetchAllPagedIn<T, string>(deliveryIds, (chunk, lo, hi) =>
    db
      .from('run_deliveries')
      .select('delivery_id, runs ( run_id, run_number, status )')
      .in('delivery_id', chunk)
      .order('run_id')
      .order('delivery_id')
      .range(lo, hi) as unknown as PageLike<T>,
    { concurrency: 3 },
  )
  if (res.error) throw new Error(res.error)
  return res.rows
}

export async function readEmptiesLoanRows<T>(db: Db, additionIds: readonly string[]): Promise<T[]> {
  const res = await fetchAllPagedIn<T, string>(additionIds, (chunk, lo, hi) =>
    db
      .from('bin_loans')
      .select('loan_id, delivery_addition_id, bin_count, container_type, notes, issued_at')
      .in('delivery_addition_id', chunk)
      .is('deleted_at', null)
      .order('issued_at', { ascending: true })
      .order('loan_id')
      .range(lo, hi) as unknown as PageLike<T>,
    { concurrency: 3 },
  )
  if (res.error) throw new Error(res.error)
  return res.rows
}

// delivery_tipping_summary returns ONE row per tipped delivery, and PostgREST
// caps a function's result at max_rows = 1000 (supabase/config.toml). Called
// with every id on a business past 1,000 tipped deliveries it came back short
// and the older rows read 0 kg tipped (U14 D7). The ids ride in a POST body, so
// the URL is no limit; chunk them so no call can return more than the cap.
const TIPPING_CHUNK = 500
const TIPPING_ROW_CAP = 1000
type RpcDb = { rpc: (fn: string, args: any) => PromiseLike<{ data: unknown; error: { message: string } | null }> }

export async function readTippingSummaryRows<T>(db: RpcDb, deliveryIds: readonly string[]): Promise<T[]> {
  const out: T[] = []
  for (const chunk of inChunks(deliveryIds, TIPPING_CHUNK)) {
    const { data, error } = await withReadTimeout(db.rpc('delivery_tipping_summary', { p_ids: chunk }), 'delivery_tipping_summary chunk')
    if (error) throw error
    // One row per id, so a cap-sized answer means rows were cut off: fail, never show short kg.
    if (Array.isArray(data) && data.length >= TIPPING_ROW_CAP) throw new Error('delivery_tipping_summary hit the row cap')
    out.push(...((data ?? []) as T[]))
  }
  return out
}
